import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextInput } from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { ImageField } from 'src/components/Storefront/ImageField';
import { SortableList, SortableRow, moveItem } from 'src/components/Storefront/SortableList';
import { StorefrontGalleryItemDto } from 'src/services/apis/types';

/** The API's caps (spec §3): 24 items, 120 characters of caption. Exceeding one is a 422 the
 * editor should never let the owner send. */
export const GALLERY_MAX = 24;
export const GALLERY_CAPTION_MAX = 120;

/** The editor holds a caption as a string: `null` is the wire value for "no caption", and typing
 * `null` into a Carbon TextInput is not a thing. A row with no image yet holds `fileId: null`. */
export type GalleryRow = { fileId: string | null; caption: string };

/**
 * Coerces whatever the jsonb column holds into rows the editor can render. The generated entity
 * types `gallery` loosely (Swagger cannot describe a jsonb array's items), so this is the one
 * place that decides what a stored item looks like — and it never throws on a hand-edited row.
 */
export function toGalleryRows(input: unknown): GalleryRow[] {
  const stored = Array.isArray(input) ? input : [];
  return stored
    .filter((raw): raw is { fileId?: unknown; caption?: unknown } => typeof raw === 'object' && raw !== null)
    .map((raw) => ({
      fileId: typeof raw.fileId === 'string' && raw.fileId !== '' ? raw.fileId : null,
      caption: typeof raw.caption === 'string' ? raw.caption : '',
    }));
}

/**
 * The payload. A row with no image is not a gallery item — the API's `fileId` is required, so an
 * unfinished row is dropped rather than sent as `''`. The list is capped here as well as in the
 * Add button, because a row stored before the cap existed must not turn a save into a 422.
 */
export function toGalleryPayload(rows: GalleryRow[]): StorefrontGalleryItemDto[] {
  return rows
    .filter((row) => typeof row.fileId === 'string' && row.fileId !== '')
    .slice(0, GALLERY_MAX)
    .map((row) => {
      const fileId = row.fileId as string;
      const caption = row.caption.trim();
      // A blank caption omits the key instead of sending `null`: Swagger expresses no nullability,
      // so the generated DTO is `caption?: string`, and the server treats missing and null alike.
      return caption === '' ? { fileId } : { fileId, caption };
    });
}

interface GalleryEditorProps {
  items: GalleryRow[];
  onChange: (next: GalleryRow[]) => void;
}

const GalleryEditor = React.memo(({ items, onChange }: GalleryEditorProps) => {
  const { t } = useTranslation();

  const update = useCallback(
    (index: number, patch: Partial<GalleryRow>) => {
      onChange(items.map((row, position) => (position === index ? { ...row, ...patch } : row)));
    },
    [items, onChange]
  );

  const remove = useCallback(
    (index: number) => {
      onChange(items.filter((_row, position) => position !== index));
    },
    [items, onChange]
  );

  const add = useCallback(() => {
    onChange([...items, { fileId: null, caption: '' }]);
  }, [items, onChange]);

  const reorder = useCallback(
    (from: number, to: number) => {
      onChange(moveItem(items, from, to));
    },
    [items, onChange]
  );

  const atCap = items.length >= GALLERY_MAX;

  return (
    <fieldset className={'storefront-extras__group'}>
      <legend className={'cds--visually-hidden'}>{t('storefront.extras.gallery')}</legend>
      <p className={'storefront-extras__hint'}>{t('storefront.extras.galleryHint')}</p>

      {items.length === 0 && <p className={'storefront-extras__empty'}>{t('storefront.extras.galleryEmpty')}</p>}

      {items.length > 0 && (
        <SortableList ids={items.map((_row, index) => String(index))} onReorder={reorder}>
          {items.map((row, index) => (
            <SortableRow
              key={index}
              id={String(index)}
              removeLabel={t('storefront.extras.removeImage')}
              onRemove={() => remove(index)}
            >
              <ImageField
                title={t('storefront.extras.image')}
                description={t('storefront.extras.imageHint')}
                aspectRatio={'4x3'}
                cropperAspectRatio={4 / 3}
                value={row.fileId}
                onChange={(fileId) => update(index, { fileId })}
              />
              <TextInput
                id={`gallery-caption-${index}`}
                labelText={t('storefront.extras.caption')}
                helperText={t('storefront.extras.captionHint')}
                maxLength={GALLERY_CAPTION_MAX}
                value={row.caption}
                onChange={(event) => update(index, { caption: event.target.value })}
              />
            </SortableRow>
          ))}
        </SortableList>
      )}

      <div className={'storefront-extras__add'}>
        <Button renderIcon={Add} kind={'tertiary'} size={'sm'} disabled={atCap} onClick={add}>
          {t('storefront.extras.addImage')}
        </Button>
        {atCap && <p className={'storefront-extras__empty'}>{t('storefront.extras.galleryFull')}</p>}
      </div>
    </fieldset>
  );
});

GalleryEditor.displayName = 'GalleryEditor';

export { GalleryEditor };
export default GalleryEditor;
